import chunk from 'lodash/chunk';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import reduce from 'lodash/reduce';
import sortBy from 'lodash/sortBy';
import map from 'lodash/map';
import EventEmitter from 'wolfy87-eventemitter';
import { inject, injectable } from 'inversify';
import MESSAGE_TYPES from '../../../constants/MESSAGE_TYPES';
import { translateTicket } from '../../../helpers/tickets';
import Messager from '../../messager';
import { IFloor } from '../../../types/floor';
import { ISector, IStandingSector, ITicketsControllerSector } from '../../../types/sector';
import { ISelectedTicket, ITicket } from '../../../types/ticket';
import { isSvgPath } from '../../../helpers/svg';
import widgetSources from '../../../sources/widget';
import { CATEGORIES_EVENTS, META_EVENTS } from '../../../constants/CONTROLLERS_EVENT_TYPES';
import type { ITicketsController, ITicketsEvents } from './tickets.interface';
import { ZOOM_MODULE_IDS } from '../zoom/zoom.types';
import { captureException } from '../../../helpers/sentry';
import { BACKGROUND_MODULE_IDS } from '../background/background.types';
import type { IBackgroundController } from '../background/background.interface';
import type { IZoomController } from '../zoom/zoom.interface';
import { STATE_MODULE_IDS } from '../state/state.types';
import type { IStateController } from '../state/state.interface';
import { TICKETS_MODULE_IDS } from './tickets.types';
import { uniq } from 'lodash';

function updateSelectedTickets(selectedTickets: Array<ITicket>, ticket: ITicket) {
  const {
    meta: { isPeriodical },
    selectedEvent,
  } = window.Widget.metaController;

  return selectedTickets.map((selectedTicket) => {
    if (selectedTicket.announcement_id !== undefined) {
      if (selectedTicket.announcement_id === ticket.announcement_id) {
        return ticket;
      }
    }

    if (isPeriodical) {
      if (selectedTicket.id === ticket.id && selectedTicket.selectedEvent === selectedEvent) {
        return ticket;
      }

      return selectedTicket;
    }

    if (selectedTicket.id === ticket.id) {
      return ticket;
    }

    return selectedTicket;
  });
}

@injectable()
export class TicketsController extends EventEmitter implements ITicketsController {
  selectedTickets = [];
  selectedTicketsIds = new Set();
  selectedTicketsCounts = {};
  sectors = [];
  sectorsPaths = {};
  tickets = [];
  pricesSet = new Set() as Set<number>;
  leftoversSectors = null;
  callback: (selectedTickets: Array<ITicket>) => void;
  loadTicketsRetries = 0;

  @inject(BACKGROUND_MODULE_IDS.BACKGROUND_CONTROLLER)
  public backgroundController: IBackgroundController;
  @inject(ZOOM_MODULE_IDS.ZOOM_CONTROLLER) public zoomController: IZoomController;
  @inject(STATE_MODULE_IDS.STATE_CONTROLLER) public stateController: IStateController;

  constructor(@inject(TICKETS_MODULE_IDS.TICKETS_EVENTS) public events: ITicketsEvents) {
    super();

    this.addListener(this.events.ADD, this.handleChange);
    this.addListener(this.events.REMOVE, this.handleChange);
  }

  reset() {
    this.selectedTickets = [];
    this.selectedTicketsIds = new Set();
    this.pricesSet = new Set();
    this.selectedTicketsCounts = {};
    this.sectors = [];
    this.tickets = [];
    this.leftoversSectors = null;
  }

  handleVerifyRecaptcha = async () => {
    return new Promise((resolve) => {
      const { token } = window.Widget.settings;

      if (!token) {
        return resolve(undefined);
      }

      let scriptEle = document.createElement('script');

      scriptEle.setAttribute('src', `https://www.google.com/recaptcha/api.js?render=${token}`);

      document.body.appendChild(scriptEle);

      // success event
      scriptEle.addEventListener('load', () => {
        window.grecaptcha.ready(function () {
          window.grecaptcha
            .execute(window.Widget.settings.token, { action: 'tickets' })
            .then(function (token) {
              resolve(token);
            });
        });
      });
      // error event
      scriptEle.addEventListener('error', (ev) => {
        console.log('Error on loading file', ev);
        resolve(undefined);
        captureException(ev);
      });
    });
  };

  getTickets = async () => {
    const { Widget } = window;
    let uuid = Widget.uuid;

    if (Widget.settings.children && Widget.settings.children.length) {
      const childWidget = Widget.settings.children.find(
        (child) => Widget.metaController.selectedDate === child.date,
      );
      uuid = childWidget ? childWidget.widget : uuid;
    }

    return widgetSources
      .tickets()
      .then((response) => {
        if (response.data) {
          const { selectedTickets } = Widget.settings;
          const {
            categories,
            sectors,
            leftover_threshold: leftoverThreshold,
            is_table: isTable,
          } = response.data;

          Widget.metaController.updateLeftoverThreshold(leftoverThreshold);

          if (isTable && !Widget.settings.withAnnouncements && !Widget?.isShowTable) {
            Widget.setSchemeMode(false);
          }

          if (!isTable) {
            Widget.setSchemeMode(true);
          }

          if (categories && sectors) {
            Widget.withCategories = true;
            Widget.isHybrid = true;

            Widget.categoriesController.setCategories(categories, selectedTickets);
            const { outlines: sectorsOutlines } = this.addTickets(sectors, selectedTickets);

            const categoriesOutlines = Widget.categoriesController.getCategoriesOutlines();

            this.backgroundController.renderOutlines({
              ...sectorsOutlines,
              ...categoriesOutlines,
            });

            const { tickets, pricesSet: ticketsPricesSet } = this;
            const { pricesSet: categoriesPricesSet } = Widget.categoriesController;

            this.emit(this.events.TOGGLE_ORDER_FORM, !tickets.length && !categoriesPricesSet.size);

            this.emit(this.events.INIT_TICKETS);

            this.emit(
              this.events.SET_PRICES,
              this.getPrices(new Set([...ticketsPricesSet, ...categoriesPricesSet])),
            );

            return;
          }

          if (categories) {
            Widget.withCategories = true;

            Widget.categoriesController.setCategories(categories, selectedTickets);

            const categoriesOutlines = Widget.categoriesController.getCategoriesOutlines();
            this.backgroundController.renderOutlines(categoriesOutlines);

            const { pricesSet, colors } = Widget.categoriesController;

            const prices = sortBy([...pricesSet]);

            Widget.categoriesController.emit(
              CATEGORIES_EVENTS.SET_PRICES,
              prices.map((p) => [p]),
              colors,
            );

            this.emit(this.events.TOGGLE_ORDER_FORM, !pricesSet.size);

            return;
          }

          const { outlines } = this.addTickets(sectors, selectedTickets);
          this.backgroundController.renderOutlines(outlines);

          const { pricesSet, getPrices } = this;

          this.emit(this.events.SET_PRICES, getPrices(pricesSet));
          this.emit(this.events.TOGGLE_ORDER_FORM, !pricesSet.size);
        }
      })
      .catch((e) => {
        const statusCode = get(e, 'response.status');
        if ((statusCode === 403 || statusCode === 502) && this.loadTicketsRetries < 2) {
          this.loadTicketsRetries += 1;

          return this.getTickets();
        }

        this.emit(this.events.CHECK_ERROR, e);
      });
  };

  reloadTickets() {
    const selectedTickets = this.selectedTickets;

    this.stateController.setState({
      isLoading: true,
      ticketsIsLoaded: false,
    });

    window.Widget.ticketsController.reset();
    window.Widget.pricesController.resetSelectedPrices();
    window.Widget.metaController.emit(META_EVENTS.CHANGE_SECTOR, null);

    if (window.Widget.schemeMode) {
      const { startX, startY } = window.Widget.schemeParameters;
      const { zoomRange } = this.zoomController;
      window.Widget.setZoomRange(zoomRange);
      const [newScale] = zoomRange;
      window.Widget.resetStyles();
      window.Widget.resetTransforms();
      window.Widget.translate(startX, startY);
      window.Widget.scale(newScale);
      window.Widget.update();
      this.zoomController.emit(this.zoomController.events.UPDATE);
      this.backgroundController.forceUpdate();
    }

    this.getTickets().then(() => {
      this.setSelectedTickets(selectedTickets);

      this.stateController.setState({
        isLoading: false,
        ticketsIsLoaded: true,
      });
    });
  }

  handleChange() {
    this.emit(this.events.CHANGE, this.selectedTickets);
  }

  add(ticket: ITicket) {
    const id = ticket.id || ticket.ticket_id;
    const { selectedEvent, selectedDate } = window.Widget.metaController;
    const newTicket = {
      ...ticket,
      ...(selectedEvent && { selectedEvent }),
      ...(selectedDate && { selectedDate }),
    };
    this.selectedTickets.push(newTicket);
    this.selectedTicketsIds.add(id);
    this.emit(this.events.ADD, newTicket);

    Messager.send({
      type: MESSAGE_TYPES.UPDATE_TICKETS,
      tickets: this.selectedTickets,
    });
    Messager.send({
      type: MESSAGE_TYPES.ADD_TICKET,
      ticket: translateTicket(newTicket),
    });
  }

  addPoolTicket(ticket: ITicket) {
    this.selectedTickets.push(ticket);
    this.emit(this.events.ADD, ticket);
    Messager.send({
      type: MESSAGE_TYPES.ADD_TICKET,
      ticket: translateTicket(ticket),
    });
  }

  remove(ticket: ITicket) {
    const id = ticket.id;
    const removedTicket = this.selectedTickets.find((t) => t.id === id);
    this.selectedTickets = this.selectedTickets.filter((t) => t.id !== id && t.ticket_id !== id);
    this.selectedTicketsIds.delete(id);
    this.emit(this.events.REMOVE, removedTicket);
    Messager.send({
      type: MESSAGE_TYPES.UPDATE_TICKETS,
      tickets: this.selectedTickets,
    });
    Messager.send({
      type: MESSAGE_TYPES.DELETE_TICKET,
      ticket: translateTicket(removedTicket),
    });
  }

  removePoolTicket(ticket: ITicket) {
    const id = ticket.ticket_id;
    const removedTicket = this.selectedTickets.find((t) => t.ticket_id === id);
    this.selectedTickets = this.selectedTickets.filter((t) => t.ticket_id !== id);
    this.emit(this.events.REMOVE, removedTicket);
    Messager.send({
      type: MESSAGE_TYPES.DELETE_TICKET,
      ticket: translateTicket(removedTicket),
    });
  }

  addId(id: string) {
    this.selectedTicketsIds.add(id);
  }

  removeId(id: string) {
    this.selectedTicketsIds.delete(id);
  }

  addCounterTicket(ticket: ITicket) {
    if (!ticket.count) return;

    const id = ticket.announcement_id || ticket.id;

    if (!this.selectedTicketsIds.has(id)) {
      this.selectedTickets.push(ticket);
      this.selectedTicketsIds.add(id);
    } else {
      this.selectedTickets = updateSelectedTickets(this.selectedTickets, ticket);
    }

    Messager.send({
      type: MESSAGE_TYPES.UPDATE_TICKETS,
      tickets: this.selectedTickets,
    });
    Messager.send({
      type: MESSAGE_TYPES.ADD_TICKET,
      ticket: translateTicket(ticket),
    });
    this.emit(this.events.RENDER_TICKETS);
  }

  updatePoolCounterTicket(ticket: ITicket, count: number, floor: IFloor) {
    const { id: floorId, x, y, price } = floor;
    const ticketId = ticket.announcement_id;
    const newTicket = {
      ...ticket,
      count,
    };
    this.selectedTicketsCounts[floorId] = get(this.selectedTicketsCounts, floorId, {
      x,
      y,
      price,
      tickets: {},
    });
    this.selectedTicketsCounts[floorId].tickets[ticketId] = get(
      this.selectedTicketsCounts,
      `${floorId}.${ticketId}`,
      {},
    );
    this.selectedTicketsCounts[floorId].tickets[ticketId] = newTicket;
    this.selectedTicketsCounts[floorId].price = Math.min(
      this.selectedTicketsCounts[floorId].price,
      price,
    );

    if (count === 0) {
      delete this.selectedTicketsCounts[floorId].tickets[ticketId];
    }

    if (isEmpty(this.selectedTicketsCounts[floorId].tickets)) {
      delete this.selectedTicketsCounts[floorId];
    }

    Messager.send({
      type: MESSAGE_TYPES.ADD_TICKET,
      ticket: translateTicket(newTicket),
    });

    this.emit(this.events.RENDER_TICKETS);
  }

  removeCounterTicket(ticket: ITicket) {
    const id = ticket.announcement_id || ticket.id;

    if (ticket.count === 0) {
      this.selectedTickets = this.selectedTickets.filter((item) => {
        if (item.announcement_id !== undefined) {
          return item.announcement_id !== ticket.announcement_id;
        }

        return item.id !== id;
      });
      this.selectedTicketsIds.delete(id);
    } else {
      this.selectedTickets = updateSelectedTickets(this.selectedTickets, ticket);
    }

    Messager.send({
      type: MESSAGE_TYPES.UPDATE_TICKETS,
      tickets: this.selectedTickets,
    });
    Messager.send({
      type: MESSAGE_TYPES.DELETE_TICKET,
      ticket: translateTicket(ticket),
    });
    this.emit(this.events.RENDER_TICKETS);
  }

  addTickets(sectors: Array<ISector & IStandingSector>, selectedTickets: ISelectedTicket[]) {
    const outlines: {
      path?: string;
      isStand?: boolean;
    } = {};
    const { selectedEvent, meta } = window.Widget.metaController;

    this.sectors = sectors
      ? sectors.map(
          ({
            i: sectorName,
            d: id,
            c: floorTicketsCount,
            r: rows,
            o: outline,
            p: price,
            k: hash,
            all: totalNumberSectorSeats,
            ex: extraServices,
            at,
            tt,
            extra,
            seat_x,
            seat_y,
          }) => {
            if (price) {
              price = extra?.netto_price || price;
            }
            const sectorId = meta.isPeriodical ? `${selectedEvent}_${id}` : id;

            let ticketsCount = 0;

            let minPrice = null;
            let maxPrice = null;
            const isStand = Boolean(floorTicketsCount) && rows.length === 0;
            const ticketsInSector = { [sectorId]: { rows: new Set(), tickets: {} } };

            if (outline && isSvgPath(outline)) {
              outlines[sectorId] = {
                path: outline,
                stand: isStand,
              };
            }

            let atData = at ? [at] : [];
            let ttData = tt ? [tt] : [];

            if (rows && !seat_x && !seat_y) {
              rows.forEach(({ i: rowName, s: seats }) => {
                if (seats) {
                  atData = seats.map((seat) => seat.at);
                  ttData = seats.map((seat) => seat.tt);

                  seats.forEach(
                    ({
                      x,
                      y,
                      p: price,
                      d: seatId,
                      k: hash,
                      i: name,
                      ex: extraServices,
                      extra,
                      at: seatAttribute,
                      tt: type,
                      our,
                    }) => {
                      if (price) {
                        price = extra?.netto_price || price;
                        const id = meta.isPeriodical ? `${selectedEvent}_${seatId}` : seatId;

                        this.pricesSet.add(price);

                        minPrice = minPrice || price;
                        maxPrice = maxPrice || price;

                        minPrice = Math.min(minPrice, price);
                        maxPrice = Math.max(maxPrice, price);

                        ticketsCount += 1;

                        const ticket = {
                          id,
                          x,
                          y,
                          price,
                          our,
                          k: hash,
                          seat: name,
                          ex: extraServices,
                          extra,
                          at: seatAttribute,
                          tt: type,
                          sector: sectorName,
                          row: rowName,
                          selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
                          selectedDate: meta.isPeriodical
                            ? meta.dates.find((data) => data.id === selectedEvent).date
                            : undefined,
                        };

                        this.tickets.push(ticket);

                        ticketsInSector[sectorId].rows.add(ticket.row);
                        ticketsInSector[sectorId].tickets[ticket.row] =
                          ticketsInSector[sectorId].tickets[ticket.row] || [];
                        ticketsInSector[sectorId].tickets[ticket.row].unshift(ticket);

                        if (selectedTickets && selectedTickets.length) {
                          const findedSelectedTickets = selectedTickets.find(
                            (selectedTicket) => selectedTicket.id === id,
                          );

                          if (findedSelectedTickets) {
                            this.selectedTickets.push(ticket);
                            this.selectedTicketsIds.add(id);
                          }
                        }
                      }
                    },
                  );
                }
              });
            }

            let sectorInfo: ITicketsControllerSector = {
              label: sectorName,
              id: sectorId,
              minPrice: minPrice || 0,
              maxPrice: maxPrice || 0,
              ticketsCount,
              all: totalNumberSectorSeats,
              ex: extraServices,
              extra,
              at: uniq(atData.map(Number)),
              tt: uniq(ttData.map(Number)),
              data: {
                ...ticketsInSector[sectorId],
                rows: [...ticketsInSector[sectorId].rows],
              },
            };

            const percent =
              totalNumberSectorSeats > 0 ? (ticketsCount * 100) / totalNumberSectorSeats : 100;

            if (
              !isStand &&
              percent <= window.Widget.metaController.leftoverThreshold &&
              ticketsCount > 0
            ) {
              this.leftoversSectors = this.leftoversSectors || {};
              this.leftoversSectors[sectorName] = {
                count: ticketsCount,
                path: outline,
                minPrice: minPrice || 0,
              };
            }

            if (floorTicketsCount && rows.length === 0) {
              sectorInfo = {
                ...sectorInfo,
                count: floorTicketsCount,
                price,
                minPrice: price,
                maxPrice: price,
                k: hash,
                x: seat_x,
                y: seat_y,
                ticketsCount: floorTicketsCount,
                all: totalNumberSectorSeats,
                ex: extraServices,
                extra,
              };

              this.tickets.push({
                price,
                x: sectorInfo.x,
                y: sectorInfo.y,
                k: sectorInfo.k,
                id: sectorInfo.id,
                ex: sectorInfo.ex,
                extra,
                isStand: true,
                sector: sectorName,
              });

              this.pricesSet.add(price);

              if (selectedTickets && selectedTickets.length) {
                const findedSelectedTickets = selectedTickets.find(
                  (selectedTicket) => selectedTicket.id === sectorInfo.id,
                );

                if (findedSelectedTickets) {
                  this.selectedTickets.push({
                    ...sectorInfo,
                    count: findedSelectedTickets.count,
                    selectedEvent: meta.isPeriodical ? selectedEvent : undefined,
                    selectedDate: meta.isPeriodical
                      ? meta.dates.find((data) => data.id === selectedEvent).date
                      : undefined,
                  });
                  this.selectedTicketsIds.add(sectorInfo.id);
                }
              }
            }

            return sectorInfo;
          },
        )
      : [];

    return { outlines };
  }

  getPrices(pricesSet: Set<number>) {
    const sortedPrices = sortBy([...pricesSet]);
    const MAGIC_VALUE_TO_DECREASE_COUNT_PRICES_IN_ONE_RANGE = 5;
    const MAX_COUNT_PRICE_RANGES = 8;
    const maxCountPricesInOnePriceRange = Math.round(
      sortedPrices.length / MAGIC_VALUE_TO_DECREASE_COUNT_PRICES_IN_ONE_RANGE,
    );

    if (maxCountPricesInOnePriceRange === 0 && sortedPrices.length > 0) {
      return chunk(sortedPrices, 1);
    }

    const pricesCountInOneRange = sortedPrices.length <= 10 ? 2 : 5;
    let prices = chunk(sortedPrices, pricesCountInOneRange);

    if (prices.length > MAX_COUNT_PRICE_RANGES) {
      prices = chunk(sortedPrices, maxCountPricesInOnePriceRange);
    }

    return prices;
  }

  findSector(sectorId: string) {
    return this.sectors.find((sector) => sector.id === sectorId);
  }

  unselect() {
    this.selectedTickets = [];
    this.selectedTicketsIds = new Set();
    window.Widget.settings.selectedTickets = [];
    this.emit(this.events.REMOVE);

    Messager.send({
      type: MESSAGE_TYPES.UPDATE_TICKETS,
      tickets: this.selectedTickets,
    });
  }

  showTicketsCountModal(id, ticket?) {
    const sector = this.sectors.find((s) => s.id === id);

    if (sector && sector.count) {
      this.emit(this.events.SHOW_TICKETS_COUNT_MODAL, sector, ticket);
    }
  }

  findTicketInSelected(currentSeat) {
    if (window.Widget.isPool) {
      return this.selectedTickets.find((seat) => seat.ticket_id === currentSeat.ticket_id);
    }

    return this.selectedTickets.find((seat) => seat.id === currentSeat.id);
  }

  unselectTicketsIds(tickets: Array<ITicket>) {
    if (head(tickets).count) {
      const deletedTicket = head(tickets);

      this.selectedTicketsCounts = reduce(
        this.selectedTicketsCounts,
        (result, floor: IFloor, floorId: string) => {
          const ticketsInFloor = reduce(
            floor.tickets,
            (acc, ticket, ticketId) => {
              if (Number(ticketId) !== deletedTicket.id) {
                acc[ticketId] = ticket;
              }

              return acc;
            },
            {},
          );

          if (!isEmpty(ticketsInFloor)) {
            result[floorId] = { ...floor, tickets: ticketsInFloor };
          }

          return result;
        },
        {},
      );

      return this.emit(this.events.RENDER_TICKETS);
    }

    const ticketsIds = new Set(tickets.map((ticket) => ticket.id));
    this.selectedTickets = this.selectedTickets.reduce((result, ticket) => {
      if (!ticketsIds.has(String(ticket.ticket_id))) {
        result.push(ticket);
        return result;
      }
      this.selectedTicketsIds.delete(ticket.ticket_id);

      return result;
    }, []);

    this.emit(this.events.RENDER_TICKETS);
  }

  deleteMarketplaceTicket(ticket: ITicket) {
    this.setSelectedTickets(this.selectedTickets.filter((t) => t.id !== ticket.id));
    this.emit(this.events.REMOVE, ticket);
  }

  updateMarketplaceTicket(ticketData: { id: string; newCount: number }) {
    const { id, newCount } = ticketData;

    const newSelectedTickets = map(this.selectedTickets, (ticket) => {
      if (ticket.id === id) {
        return {
          ...ticket,
          count: newCount,
        };
      }

      return ticket;
    });

    this.setSelectedTickets(newSelectedTickets);
  }

  emitUpdpateTickets(ticketData) {
    this.emit(this.events.UPDATE_COUNT, ticketData);
  }

  setSelectedTickets(tickets: Array<ITicket>) {
    this.selectedTicketsIds = new Set(tickets.map((ticket) => ticket.id));
    this.selectedTickets = tickets;

    this.emit(this.events.RENDER_TICKETS);
    this.emit(this.events.SET_NEW_SELECTED_TICKETS);
  }
}
